import React, { useState, useRef, useEffect } from 'react';
import { X, Send, ChevronDown, Loader, Zap } from 'lucide-react';
import { GUIDES, rechercherGuide, REPONSES_INCONNUES } from './knowledge';
import axios from 'axios';

// Rendu Markdown simple
function renderMarkdown(text) {
  if (!text) return null;
  // Gras
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );
}

// Rendu Markdown complet pour les réponses IA
function renderAIResponse(text) {
  if (!text) return null;
  const lines = text.split('\n');
  return lines.map((line, i) => {
    if (!line.trim()) return <br key={i} />;
    // Titres
    if (line.startsWith('### ')) return <p key={i} className="font-bold text-slate-800 mt-2 mb-1">{line.slice(4)}</p>;
    if (line.startsWith('## ')) return <p key={i} className="font-bold text-slate-800 text-base mt-2 mb-1">{line.slice(3)}</p>;
    // Listes numérotées
    const numMatch = line.match(/^(\d+)\.\s+(.+)/);
    if (numMatch) return (
      <div key={i} className="flex gap-2 my-0.5">
        <span className="w-5 h-5 rounded-full bg-gold-100 text-gold-700 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{numMatch[1]}</span>
        <span>{renderMarkdown(numMatch[2])}</span>
      </div>
    );
    // Listes à puces
    if (line.startsWith('- ') || line.startsWith('• ')) return (
      <div key={i} className="flex gap-2 my-0.5">
        <span className="text-gold-500 mt-0.5">•</span>
        <span>{renderMarkdown(line.slice(2))}</span>
      </div>
    );
    // ⚠️ Urgence
    if (line.includes('⚠️') || line.includes('🚨')) return (
      <p key={i} className="bg-red-50 border-l-2 border-red-400 pl-2 py-0.5 my-1 text-red-700 font-medium">{line}</p>
    );
    return <p key={i} className="my-0.5">{renderMarkdown(line)}</p>;
  });
}

function Message({ msg }) {
  if (msg.type === 'user') {
    return (
      <div className="flex justify-end mb-3">
        <div className="bg-navy-700 text-white rounded-2xl rounded-tr-sm px-4 py-2 max-w-[80%] text-sm">
          {msg.text}
        </div>
      </div>
    );
  }

  if (msg.type === 'loading') {
    return (
      <div className="flex gap-2 mb-3">
        <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-sm flex-shrink-0">🤵</div>
        <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
          <div className="flex items-center gap-2 text-slate-400">
            <Loader size={14} className="animate-spin" />
            <span className="text-xs">Georges réfléchit...</span>
          </div>
        </div>
      </div>
    );
  }

  if (msg.type === 'ai') {
    return (
      <div className="flex gap-2 mb-3">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-navy-700 to-navy-900 flex items-center justify-center text-sm flex-shrink-0 mt-1">🤵</div>
        <div className="bg-white border border-blue-100 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%] shadow-sm">
          <div className="flex items-center gap-1 mb-2">
            <Zap size={10} className="text-gold-500" />
            <span className="text-xs text-gold-500 font-medium">Georges IA</span>
          </div>
          <div className="text-sm text-slate-700 space-y-0.5">
            {renderAIResponse(msg.text)}
          </div>
        </div>
      </div>
    );
  }

  if (msg.type === 'guide') {
    const guide = GUIDES[msg.guideKey];
    return (
      <div className="flex gap-2 mb-3">
        <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-sm flex-shrink-0 mt-1">🤵</div>
        <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%] shadow-sm">
          <p className="text-sm font-semibold text-slate-700 mb-2">{guide.icone} {guide.titre}</p>
          <ol className="space-y-1.5">
            {guide.etapes.map((etape, i) => (
              <li key={i} className="flex gap-2 text-sm text-slate-600">
                <span className="w-5 h-5 rounded-full bg-gold-100 text-gold-700 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                <span>{renderMarkdown(etape)}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    );
  }

  if (msg.type === 'menu') {
    return (
      <div className="flex gap-2 mb-3">
        <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-sm flex-shrink-0 mt-1">🤵</div>
        <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%] shadow-sm">
          <p className="text-sm text-slate-700 mb-3">{renderMarkdown(msg.text)}</p>
          <div className="grid grid-cols-2 gap-1.5">
            {Object.entries(GUIDES).map(([key, guide]) => (
              <button key={key} onClick={() => msg.onSelect(key)}
                className="text-left text-xs bg-slate-50 hover:bg-gold-50 hover:text-gold-700 border border-slate-200 hover:border-gold-300 rounded-lg px-2.5 py-2 transition-colors">
                {guide.icone} {guide.titre}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2 mb-3">
      <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-sm flex-shrink-0 mt-1">🤵</div>
      <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-2 max-w-[85%] shadow-sm">
        <p className="text-sm text-slate-700">{renderMarkdown(msg.text)}</p>
      </div>
    </div>
  );
}

export default function GeorgesAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [iaActive, setIaActive] = useState(false);
  const [sessionId] = useState(() => 'session_' + Date.now());
  const messagesEndRef = useRef(null);
  const inactivityTimer = useRef(null);
  const INACTIVITY_DELAY = 3 * 60 * 1000; // 3 minutes

  useEffect(() => {
    axios.get('/api/georges/status').then(r => setIaActive(r.data.ia_active)).catch(() => {});
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-fermeture après inactivité
  const resetInactivityTimer = () => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      setIsMinimized(true);
    }, INACTIVITY_DELAY);
  };

  useEffect(() => {
    if (isOpen && !isMinimized) {
      resetInactivityTimer();
    }
    return () => { if (inactivityTimer.current) clearTimeout(inactivityTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isMinimized, messages]);

  const addMessage = (msg) => {
    setMessages(prev => [...prev, { id: Date.now() + Math.random(), ...msg }]);
  };

  const removeLoading = () => {
    setMessages(prev => prev.filter(m => m.type !== 'loading'));
  };

  const handleOpen = () => {
    setIsOpen(true);
    setIsMinimized(false);
    if (!hasGreeted) {
      setHasGreeted(true);
      setTimeout(() => {
        addMessage({
          type: 'text',
          text: iaActive
            ? "Bonjour ! Je suis **Georges**, votre majordome digital IA. 🤵\nJe connais vos protocoles, la législation et les transmissions des enfants."
            : "Bonjour ! Je suis **Georges**, votre majordome digital. 🤵\nJe suis là pour vous guider dans l'utilisation de l'application."
        });
        setTimeout(() => {
          addMessage({ type: 'menu', text: "Sur quel sujet puis-je vous aider ?", onSelect: handleGuideSelect });
        }, 500);
      }, 300);
    }
  };

  const handleGuideSelect = (guideKey) => {
    addMessage({ type: 'user', text: GUIDES[guideKey].titre });
    setTimeout(() => {
      addMessage({ type: 'guide', guideKey });
      setTimeout(() => addMessage({ type: 'text', text: "Autre chose ?" }), 400);
    }, 300);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading) return;
    setInput('');
    addMessage({ type: 'user', text });

    // Si l'IA est active, utiliser Claude
    if (iaActive) {
      setIsLoading(true);
      addMessage({ type: 'loading' });
      try {
        const res = await axios.post('/api/georges/chat', {
          message: text,
          session_id: sessionId
        });
        removeLoading();
        addMessage({ type: 'ai', text: res.data.response });
      } catch (err) {
        removeLoading();
        addMessage({ type: 'text', text: "Désolé, je n'arrive pas à me connecter. Réessayez dans un instant." });
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Fallback : guides statiques
    setTimeout(() => {
      const guideKey = rechercherGuide(text);
      if (guideKey) {
        addMessage({ type: 'guide', guideKey });
        setTimeout(() => addMessage({ type: 'text', text: "Autre chose ?" }), 400);
      } else {
        const reponse = REPONSES_INCONNUES[Math.floor(Math.random() * REPONSES_INCONNUES.length)];
        addMessage({ type: 'menu', text: reponse, onSelect: handleGuideSelect });
      }
    }, 400);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const suggestions = iaActive
    ? ["Qualité de l'air", "Enfant blessé", "Protocole fièvre", "Transmissions"]
    : ["Créer un devis", "Tarifs", "Congés"];

  return (
    <>
      {/* Bulle flottante */}
      {!isOpen && (
        <button onClick={handleOpen} className="fixed bottom-6 right-6 z-50 group" title="Georges - Votre assistant">
          <div className="relative">
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white animate-pulse" />
            <div className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-2xl group-hover:scale-110 transition-transform border-2 border-white ${iaActive ? 'bg-gradient-to-br from-navy-700 to-navy-900' : 'bg-gradient-to-br from-navy-600 to-navy-800'}`}>
              🤵
            </div>
            <div className="absolute bottom-16 right-0 bg-white rounded-2xl rounded-br-none shadow-lg px-3 py-2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity border border-slate-100">
              <p className="text-xs font-medium text-slate-700">{iaActive ? '🧠 Georges IA' : 'Besoin d\'aide ?'}</p>
              <div className="absolute bottom-0 right-3 translate-y-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white" />
            </div>
          </div>
        </button>
      )}

      {/* Fenêtre de chat */}
      {isOpen && (
        <div className={`fixed bottom-6 right-6 z-50 w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col transition-all ${isMinimized ? 'h-14' : 'h-[520px]'}`}>
          {/* Header */}
          <div className={`flex items-center justify-between px-4 py-3 rounded-t-2xl ${iaActive ? 'bg-gradient-to-r from-navy-700 to-navy-900' : 'bg-gradient-to-r from-navy-600 to-navy-800'}`}>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-lg">🤵</div>
              <div>
                <p className="text-white font-semibold text-sm">Georges</p>
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                  <p className="text-slate-300 text-xs">{iaActive ? 'Majordome IA actif' : 'Assistant guides'}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setIsMinimized(!isMinimized)} className="text-white/70 hover:text-white p-1 rounded transition-colors">
                <ChevronDown size={16} className={`transition-transform ${isMinimized ? 'rotate-180' : ''}`} />
              </button>
              <button onClick={() => setIsOpen(false)} className="text-white/70 hover:text-white p-1 rounded transition-colors">
                <X size={16} />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 bg-slate-50 space-y-1">
                {messages.map((msg) => <Message key={msg.id} msg={msg} />)}
                <div ref={messagesEndRef} />
              </div>

              {/* Suggestions rapides */}
              <div className="px-3 py-2 bg-white border-t border-slate-100 flex gap-1.5 overflow-x-auto no-scrollbar">
                {suggestions.map((sugg) => (
                  <button key={sugg}
                    onClick={() => { setInput(sugg); setTimeout(() => { const e = { key: 'Enter', shiftKey: false, preventDefault: () => {} }; }, 50); }}
                    className="text-xs whitespace-nowrap bg-slate-100 hover:bg-gold-50 hover:text-gold-700 text-slate-600 rounded-full px-3 py-1 transition-colors">
                    {sugg}
                  </button>
                ))}
              </div>

              {/* Input */}
              <div className="px-3 py-3 bg-white rounded-b-2xl border-t border-slate-100 flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={iaActive ? "Posez n'importe quelle question..." : "Posez votre question..."}
                  disabled={isLoading}
                  className="flex-1 text-sm border border-slate-200 rounded-full px-4 py-2 focus:outline-none focus:border-navy-400 focus:ring-2 focus:ring-navy-100 disabled:bg-slate-50"
                />
                <button onClick={handleSend} disabled={!input.trim() || isLoading}
                  className="w-9 h-9 bg-navy-700 hover:bg-navy-800 disabled:bg-slate-200 text-white rounded-full flex items-center justify-center transition-colors">
                  {isLoading ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
